import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';

import { Scenario } from '@/api/rest/resources/types/fact';
import { NRPMode } from '@/api/rest/resources/types/scenarioForecast';

type ScenarioSelectorFields = {
  biophysical_horizon: number;
  climate_scenario: string;
  nrp_mode: string;
};

type EditScenarioContextValue = {
  form: UseFormReturn<ScenarioSelectorFields>;
  onSubmit: () => void;
  values: ScenarioSelectorFields;
  isDemo: boolean;
};

export const EditScenarioContext = createContext<EditScenarioContextValue | null>(null);

export const useCreateEditScenario = ({ isDemo = false }: { isDemo?: boolean } = {}): EditScenarioContextValue => {
  const queryClient = useQueryClient();

  const form = useForm<ScenarioSelectorFields>({
    defaultValues: {
      biophysical_horizon: 10,
      climate_scenario: Scenario.moderate,
      nrp_mode: NRPMode.cost_of_equity,
    },
  });

  const onSubmit: SubmitHandler<ScenarioSelectorFields> = () => {
    queryClient.invalidateQueries({ queryKey: ['scenario-forecast', 'physical-risk'] });
    queryClient.invalidateQueries({ queryKey: ['scenario-forecast', 'financial-impact'] });
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    values: form.watch(),
    isDemo,
  };
};

export const useEditScenario = (): EditScenarioContextValue => {
  const ctx = useContext(EditScenarioContext);
  if (!ctx) throw new Error('useEditScenario must be used within EditScenarioContext.Provider');
  return ctx;
};
